import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import QueryProvider from './providers/QueryProvider';
import { NotificationProvider } from './components/ui/NotificationSystem';
import { OnboardingProvider } from './context/OnboardingContext';
import OnboardingWrapper from './components/onboarding/OnboardingWrapper';
import ErrorBoundary from './components/ui/ErrorBoundary';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import OfflineStatus from './components/pwa/OfflineStatus';
import Navbar from './components/Navbar';
import { ScrollToHash } from './components/ScrollToHash';
import Hero from './components/Hero';
import Features from './components/Features';
import Solutions from './components/Solutions';
import ProductTour from './components/marketing/ProductTour';
import Pricing from './components/Pricing';
import ContactForm from './components/marketing/ContactForm';
import Footer from './components/Footer';
import SolutionDetail from './pages/SolutionDetail';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminLayout from './layouts/AdminLayout';
import PrivateRoute from './components/PrivateRoute';
import { Seo } from './components/marketing/Seo';
import { PermissionGuard } from './components/auth/PermissionGuard';
import { Permissions } from './types/auth';

// Lazy-loaded admin pages for code splitting
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const ESGReporting = React.lazy(() => import('./pages/admin/ESGReporting'));
const ESGMetrics = React.lazy(() => import('./pages/admin/ESGMetrics'));
const DataImport = React.lazy(() => import('./pages/admin/DataImport'));
const DataAnalysis = React.lazy(() => import('./pages/admin/DataAnalysis'));
const CarbonCalculator = React.lazy(() => import('./pages/admin/CarbonCalculator'));
const Goals = React.lazy(() => import('./pages/admin/Goals'));
const AIInsightsPage = React.lazy(() => import('./pages/admin/AIInsights'));
const Reports = React.lazy(() => import('./pages/admin/Reports'));
const SupplyChain = React.lazy(() => import('./pages/admin/SupplyChain'));
const Compliance = React.lazy(() => import('./pages/admin/Compliance'));
const ComplianceEvaluation = React.lazy(() => import('./pages/admin/ComplianceEvaluation'));
const Scenarios = React.lazy(() => import('./pages/admin/Scenarios'));
const Stakeholder = React.lazy(() => import('./pages/admin/Stakeholder'));
const Profile = React.lazy(() => import('./pages/admin/Profile'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const CustomDashboardPage = React.lazy(() => import('./pages/admin/CustomDashboardPage'));
const AuditLogs = React.lazy(() => import('./pages/admin/AuditLogs'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const WaterManagement = React.lazy(() => import('./pages/admin/WaterManagement'));
const GoldenLabelling = React.lazy(() => import('./pages/admin/GoldenLabelling'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
    </div>
  );
}

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryProvider>
          <NotificationProvider>
            <OnboardingProvider>
              <LanguageProvider>
                <AuthProvider>
                <OnboardingWrapper>
                  <Router>
            <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
              <Navbar />
              <OfflineStatus />
              <PWAInstallPrompt />
              <ScrollToHash />
              <Routes>
              <Route path="/" element={
                <main className="pt-16">
                  <Seo />
                  <Hero />
                  <Features />
                  <Solutions />
                  <ProductTour />
                  <Pricing />
                  <ContactForm />
                </main>
              } />
              <Route path="/solutions/:slug" element={<SolutionDetail />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Admin Routes - lazy loaded */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <AdminLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<LazyPage><Dashboard /></LazyPage>} />
                <Route path="reporting" element={<LazyPage><ESGReporting /></LazyPage>} />
                <Route path="metrics" element={<LazyPage><ESGMetrics /></LazyPage>} />
                <Route path="calculator" element={<LazyPage><CarbonCalculator /></LazyPage>} />
                <Route path="goals" element={<LazyPage><Goals /></LazyPage>} />
                <Route path="supply-chain" element={<LazyPage><SupplyChain /></LazyPage>} />
                <Route path="compliance" element={<LazyPage><Compliance /></LazyPage>} />
                <Route path="compliance-evaluation" element={<LazyPage><ComplianceEvaluation /></LazyPage>} />
                <Route path="scenarios" element={<LazyPage><Scenarios /></LazyPage>} />
                <Route path="stakeholder" element={<LazyPage><Stakeholder /></LazyPage>} />
                <Route path="ai-insights" element={<LazyPage><AIInsightsPage /></LazyPage>} />
                <Route path="reports" element={<LazyPage><Reports /></LazyPage>} />
                <Route path="import" element={<LazyPage><DataImport /></LazyPage>} />
                <Route path="analysis" element={<LazyPage><DataAnalysis /></LazyPage>} />
                <Route path="custom-dashboard" element={<LazyPage><CustomDashboardPage /></LazyPage>} />
                <Route path="profile" element={<LazyPage><Profile /></LazyPage>} />
                <Route path="settings" element={<LazyPage><Settings /></LazyPage>} />
                <Route path="audit-logs" element={<LazyPage><AuditLogs /></LazyPage>} />
                <Route path="users" element={<LazyPage><UserManagement /></LazyPage>} />
                <Route path="water" element={<LazyPage><WaterManagement /></LazyPage>} />
                <Route
                  path="golden-labelling"
                  element={
                    <PermissionGuard permission={Permissions.GOLDEN_WRITE}>
                      <LazyPage><GoldenLabelling /></LazyPage>
                    </PermissionGuard>
                  }
                />
              </Route>
              </Routes>
              <Footer />
            </div>
                  </Router>
                </OnboardingWrapper>
                </AuthProvider>
              </LanguageProvider>
            </OnboardingProvider>
          </NotificationProvider>
        </QueryProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;